import { useMemo } from 'react';
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import { useGT } from 'gt-react';
import { waitForBackgroundJob } from '@/api/background-jobs/waitForBackgroundJob';
import {
  classifyLineDecisionOutcome,
  type SkippedReasonCode,
} from './lineDecisionOutcome';
import { Sd849ResubmitResultSchema } from '@erp/rpc';
import type { VdmProvinceOverrideRequiredData } from '@erp/shared-types';
import { readVdmProvinceOverrideRequired } from './vdmProvinceOverride';
import type {
  ListSD849ResponseActivitiesInput,
  ResubmitRejectedInput,
  Sd849ResubmitResult,
  SD849LineDecision,
  SD849ResponseActivity,
  TransactionVdmLink,
  VdmLinkTarget,
} from '@erp/rpc';

const ACTIVITY_PAGE = 200;

/**
 * Re-read the 849 Responses page: its rows AND its stat-card totals. They are
 * separate queries, so anything that changes a response's lines, supplier or
 * division has to invalidate both or the cards go stale.
 */
export function invalidate849ResponseListAndTotals(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.edi849Responses.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.shipAndDebit.edi849Responses.totals.key(),
  });
}

/**
 * Fetch ALL activities of the given types for a response (paging until exhausted),
 * ascending by eventAt. Used for the History timeline: it's structurally small (one
 * entry per email/evolution on a single claim), so it must never truncate — neither
 * the original `created` anchor nor the newest evolutions can drop off.
 */
export function useAllSD849ResponseActivities(
  sd849ResponseId: string | undefined,
  activityType: SD849ResponseActivity['activityType'][],
) {
  const query = useQuery({
    queryKey: ['sd849-all-activities', sd849ResponseId, ...activityType],
    enabled: Boolean(sd849ResponseId),
    queryFn: sd849ResponseId
      ? async () => {
          const all: SD849ResponseActivity[] = [];
          for (;;) {
            const res =
              await rpcClient.shipAndDebit.edi849Responses.listActivities({
                sd849ResponseId,
                activityType,
                limit: ACTIVITY_PAGE,
                offset: all.length,
                order: 'asc',
              });
            all.push(...res.activities);
            if (
              all.length >= res.pagination.total ||
              res.activities.length === 0
            )
              break;
          }
          return all;
        }
      : skipToken,
  });
  return { activities: query.data ?? [], isLoading: query.isLoading };
}

/**
 * Hook for listing the activity timeline of a single 849 response.
 *
 * Returns only the fields consumers use rather than spreading the whole
 * useQuery() result, so TanStack Query subscribes to just those fields.
 */
export function useEDI849ResponseActivities(
  sd849ResponseId: string | undefined,
  filters: Omit<ListSD849ResponseActivitiesInput, 'sd849ResponseId'> = {},
  enabled = true,
) {
  const query = useQuery({
    ...orpc.shipAndDebit.edi849Responses.listActivities.queryOptions({
      input: sd849ResponseId ? { sd849ResponseId, ...filters } : skipToken,
    }),
    enabled: Boolean(sd849ResponseId) && enabled,
  });

  return {
    activities: query.data?.activities ?? [],
    pagination: query.data?.pagination,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Comments on one 849 response — the claim's free-text notes ("chasing the
 * supplier on the 18 truncated lines"), stored as `comment_added` activities.
 *
 * One object owns the whole feature, like {@link useLineDecisions}: the caller
 * writes text and never touches query keys, mutations or toasts. There is no
 * read here on purpose — a comment IS a timeline entry, so it arrives with the
 * rest of the activity feed; these mutations just invalidate it.
 */
export interface ResponseComments {
  /**
   * Write a new comment. Resolves TRUE only once the server has it.
   *
   * The caller needs that answer: it clears the box the user typed into, and on
   * a rejected write (the edit window closed, the request failed) clearing it
   * would destroy text that exists nowhere else. An error toast is not a
   * substitute for the words someone wrote.
   */
  add: (text: string, lineIndex?: number) => Promise<boolean>;
  /** Edit an existing comment. Resolves TRUE only once the server has it. */
  edit: (activityId: string, text: string) => Promise<boolean>;
  /** Delete a comment. Nothing the user typed is at stake, so this is fire-and-forget. */
  remove: (activityId: string) => void;
  /** A write is in flight — disable the composer. */
  isSaving: boolean;
}

export function useSD849ResponseComments(
  sd849ResponseId: string | undefined,
): ResponseComments {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  /**
   * A comment lands on (or leaves) the activity timeline, so both activity
   * queries have to re-read: the paged `listActivities` and the fetch-everything
   * `sd849-all-activities` used by the History section. Invalidating the root
   * keys covers every type-filtered variant of each.
   */
  function invalidateTimeline() {
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.edi849Responses.listActivities.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: ['sd849-all-activities'],
    });
  }

  function onError(error: Error) {
    showToast({
      title: 'Failed to save comment',
      message: error.message,
      color: 'error',
    });
  }

  /**
   * Add, edit and delete all write the same timeline. A shared scope makes
   * TanStack Query run them serially in click order, so a quick edit-then-delete
   * can't race into the wrong final state.
   */
  const commentScope = { id: `sd849-comments-${sd849ResponseId ?? ''}` };

  const addMutation = useMutation({
    ...orpc.shipAndDebit.edi849Responses.addComment.mutationOptions(),
    scope: commentScope,
    onSuccess: invalidateTimeline,
    onError,
  });

  const editMutation = useMutation({
    ...orpc.shipAndDebit.edi849Responses.editComment.mutationOptions(),
    scope: commentScope,
    onSuccess: invalidateTimeline,
    onError,
  });

  const deleteMutation = useMutation({
    ...orpc.shipAndDebit.edi849Responses.deleteComment.mutationOptions(),
    scope: commentScope,
    onSuccess: invalidateTimeline,
    onError,
  });

  /**
   * `mutateAsync` rejects on failure; the mutation's own `onError` has already
   * toasted by then, so swallow it here and report the outcome as a boolean —
   * the caller's question is "may I clear the box?", not "what went wrong?".
   */
  async function succeeded(write: Promise<unknown>): Promise<boolean> {
    try {
      await write;
      return true;
    } catch {
      return false;
    }
  }

  return {
    add: async (text, lineIndex) => {
      const trimmed = text.trim();
      if (!sd849ResponseId || !trimmed) return false;
      return succeeded(
        addMutation.mutateAsync({
          sd849ResponseId,
          text: trimmed,
          // Omitted, not null: an absent lineIndex means "on the claim as a
          // whole", which is what every comment was before line scoping.
          ...(lineIndex === undefined ? {} : { lineIndex }),
        }),
      );
    },
    edit: async (activityId, text) => {
      const trimmed = text.trim();
      if (!trimmed) return false;
      return succeeded(editMutation.mutateAsync({ activityId, text: trimmed }));
    },
    remove: (activityId) => deleteMutation.mutate({ activityId }),
    isSaving:
      addMutation.isPending ||
      editMutation.isPending ||
      deleteMutation.isPending,
  };
}

/**
 * The per-line decision on a 849 response: **agree** (accept the supplier's
 * rejection, stop disputing) or **disagree** (dispute it).
 *
 * One object owns the whole feature: whether decisions are recorded for this
 * response, what each line is currently decided as, and how to change it.
 * Callers never touch the query keys, the mutations or the toasts — they ask
 * questions about a line index and call `agree` / `disagree` / `clear`.
 */
export interface LineDecisions {
  /** False when this response's supplier doesn't record decisions. */
  enabled: boolean;
  /** The recorded decision for a line (value + who + when), or undefined. */
  decisionFor: (lineIndex: number) => SD849LineDecision | undefined;
  /** Whether this line is decided as agreed (not disputing). */
  isAgreed: (lineIndex: number) => boolean;
  /** Agree with the supplier on this line (replaces a disagree). */
  agree: (lineIndex: number, note?: string) => void;
  /**
   * Record that we dispute this line. Fire-and-forget alongside the frontend
   * resubmission staging — the staging is what drives the resubmit table, this
   * only records the decision, so a failure can't block the dispute.
   */
  disagree: (lineIndex: number, note?: string) => void;
  /** Clear the decision on this line — back to undecided. */
  clear: (lineIndex: number) => void;
  /**
   * The bulk forms: ONE request for the whole selection, not one per line.
   *
   * They share the single-line mutations (and their scope), so a bulk decision
   * and a stray single click still land in the order they were made. The server
   * skips-with-a-reason per line rather than failing the batch, so a selection
   * that includes a line nobody can decide still records the rest.
   *
   * Unlike the single-line forms these AWAIT the write and resolve with the line
   * indexes the server actually recorded — never a rejected promise. The caller
   * needs that answer because a bulk dispute also stages transactions for
   * resubmission: staging lines whose decisions failed would leave the page
   * showing disputes that vanish on reload, and would let someone resubmit a
   * claim with no recorded reason behind it. An empty array means nothing was
   * recorded (the request failed, or every line was skipped).
   */
  agreeMany: (lineIndexes: number[], note?: string) => Promise<number[]>;
  disagreeMany: (lineIndexes: number[], note?: string) => Promise<number[]>;
  clearMany: (lineIndexes: number[]) => Promise<number[]>;
  /** A write is in flight — disable the buttons. */
  isSaving: boolean;
  /**
   * The recorded decisions have arrived. Callers that seed page state from them
   * need this to tell "nothing decided" from "not loaded yet".
   */
  isLoaded: boolean;
  /** Every recorded decision, oldest first. */
  all: SD849LineDecision[];
  /** How many lines carry each decision. */
  counts: { agreed: number; disagreed: number };
}

const NO_DECISIONS: SD849LineDecision[] = [];

/**
 * One VDM attached to an 844 transaction AFTER it was sent.
 *
 * Additive: the transaction keeps the VDM it actually shipped under (its
 * `liveVdmNumber`) and can accumulate any number of these alongside it.
 */
export type { TransactionVdmLink, VdmLinkTarget };

/**
 * How a link attempt ended. `override-required` is not a failure: the server is
 * asking a permitted user to confirm a Province override, and the dialog shows
 * a "Link Anyway" warning rather than an error.
 */
export type VdmLinkOutcome =
  | { status: 'linked' }
  | { status: 'failed' }
  | { status: 'override-required'; override: VdmProvinceOverrideRequiredData };

/**
 * Attaching an additional VDM to the 844 transactions behind a 849 response.
 *
 * Same controller shape as {@link useLineDecisions}: one object owns the whole
 * feature so no component knows how a link is stored or which query key holds
 * it.
 *
 * Mutations return the FULL post-write state and it is written straight into the
 * cache — deliberately not a refetch of the 849 `get`, whose payload is ~73 kB
 * typical (and megabytes at worst) for users on ~0.25 Mbps.
 */
export interface VdmLinks {
  /** The transactions a VDM can be attached to, with what's already attached. */
  targets: VdmLinkTarget[];
  isLoading: boolean;
  /**
   * The read failed. Distinct from "no targets" on purpose: the section renders
   * nothing when a claim simply has nothing to attach to, so without this a
   * broken query is indistinguishable from an empty one and the user is left
   * hunting for a control that silently declined to exist.
   */
  loadError: Error | null;
  /** A write is in flight — disable the form. */
  isSaving: boolean;
  /**
   * Attach `vdmId` to these transactions. `provinceOverride` confirms a
   * Province override the server asked for.
   */
  link: (
    vdmId: string,
    transactionIds: string[],
    options?: { note?: string; provinceOverride?: boolean },
  ) => Promise<VdmLinkOutcome>;
  /** Remove an attachment. */
  unlink: (vdmId: string, transactionIds: string[]) => void;
}

export function useVdmLinks(sd849ResponseId: string | undefined): VdmLinks {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const queryOptions =
    orpc.shipAndDebit.edi849Responses.listVdmLinkTargets.queryOptions({
      input: sd849ResponseId ? { sd849ResponseId } : skipToken,
    });
  const query = useQuery({
    ...queryOptions,
    enabled: Boolean(sd849ResponseId),
  });

  function applyResult(data: { transactions: VdmLinkTarget[] }) {
    queryClient.setQueryData(queryOptions.queryKey, {
      transactions: data.transactions,
    });
    // Attaching or removing a memo is recorded on the claim's timeline.
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.edi849Responses.listActivities.key(),
    });
  }

  function onError(error: Error) {
    // Asking to confirm a Province override is answered in the dialog.
    if (readVdmProvinceOverrideRequired(error)) return;
    showToast({
      title: 'Failed to update VDM link',
      message: error.message,
      color: 'error',
    });
  }

  /**
   * Link and unlink write the same rows, so a shared scope makes TanStack Query
   * run them serially in click order — an attach immediately followed by a
   * remove must not land the other way round.
   */
  const linkScope = { id: `sd849-vdm-links-${sd849ResponseId ?? ''}` };

  const linkMutation = useMutation({
    ...orpc.shipAndDebit.edi849Responses.linkVdmToTransactions.mutationOptions(),
    scope: linkScope,
    onSuccess: (data) => {
      applyResult(data);
      if (data.linkedCount === 0) {
        // Every selected pair already existed — the write was a no-op, and
        // silence would read as a failure.
        showToast({
          title: 'Already linked',
          message: 'That VDM is already attached to the selected transactions.',
          color: 'warning',
        });
      }
    },
    onError,
  });

  const unlinkMutation = useMutation({
    ...orpc.shipAndDebit.edi849Responses.unlinkVdmFromTransactions.mutationOptions(),
    scope: linkScope,
    onSuccess: applyResult,
    onError,
  });

  async function link(
    vdmId: string,
    transactionIds: string[],
    options: { note?: string; provinceOverride?: boolean } = {},
  ): Promise<VdmLinkOutcome> {
    if (!sd849ResponseId || transactionIds.length === 0) {
      return { status: 'failed' };
    }
    try {
      // The caller clears its selection only on success — on a rejected write
      // the user's picks must survive so they can retry.
      await linkMutation.mutateAsync({
        sd849ResponseId,
        vdmId,
        transactionIds,
        ...(options.note ? { note: options.note } : {}),
        ...(options.provinceOverride ? { provinceOverride: true } : {}),
      });
      return { status: 'linked' };
    } catch (error) {
      const override = readVdmProvinceOverrideRequired(error);
      return override
        ? { status: 'override-required', override }
        : { status: 'failed' };
    }
  }

  function unlink(vdmId: string, transactionIds: string[]) {
    if (!sd849ResponseId || transactionIds.length === 0) return;
    unlinkMutation.mutate({ sd849ResponseId, vdmId, transactionIds });
  }

  return {
    targets: query.data?.transactions ?? NO_VDM_LINK_TARGETS,
    isLoading: query.isLoading,
    loadError: query.error,
    isSaving: linkMutation.isPending || unlinkMutation.isPending,
    link,
    unlink,
  };
}

/** Stable empty array so an unloaded controller doesn't retrigger memos. */
const NO_VDM_LINK_TARGETS: VdmLinkTarget[] = [];

/**
 * Owns the line-decision state for one 849 response.
 *
 * Every mutation returns the response's full decision state, so its result is
 * written straight into the query cache instead of triggering a refetch — S&D
 * users are on ~0.25 Mbps links and the surrounding `get` payload is large.
 */
export function useLineDecisions(
  sd849ResponseId: string | undefined,
  enabled = true,
): LineDecisions {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const gt = useGT();

  const queryOptions =
    orpc.shipAndDebit.edi849Responses.listLineDecisions.queryOptions({
      input: sd849ResponseId ? { sd849ResponseId } : skipToken,
    });
  const query = useQuery({
    ...queryOptions,
    enabled: Boolean(sd849ResponseId) && enabled,
  });

  function applyResult(data: {
    decisionsEnabled: boolean;
    decisions: SD849LineDecision[];
  }) {
    queryClient.setQueryData(queryOptions.queryKey, data);
    // The decision's audit entry belongs on the activity timeline.
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.edi849Responses.listActivities.key(),
    });
  }

  function onError(error: Error) {
    showToast({
      title: 'Failed to save decision',
      message: error.message,
      color: 'error',
    });
  }

  /**
   * Turn an outcome into a toast.
   *
   * Each decision gets its OWN string rather than a shared template with the
   * verb interpolated — "accepted"/"disputed" don't slot into one sentence
   * across languages.
   */
  /**
   * The server's skip reason, in the user's language.
   *
   * The RPC also returns English prose (`reason`) for logs and the audit trail;
   * showing that on a translated screen is the bug this avoids. An unknown or
   * absent code (an older server) falls back to a generic line.
   */
  function skipReasonText(code: SkippedReasonCode | null): string {
    if (code === 'not-decidable') {
      return gt('the supplier already credited it in full', {
        context:
          'ship-and-debit 849 reason a line could not be decided — the supplier accepted it',
      });
    }
    if (code === 'line-not-found') {
      return gt('that line is no longer on this response', {
        context:
          'ship-and-debit 849 reason a line could not be decided — the line is missing',
      });
    }
    return gt("it can't be decided", {
      context: 'ship-and-debit 849 generic reason a line could not be decided',
    });
  }

  /**
   * Turn an outcome into a toast.
   *
   * Each decision gets its OWN string rather than a shared template with the
   * verb interpolated — "accepted"/"disputed" don't slot into one sentence
   * across languages.
   */
  function reportDecisionOutcome(
    outcome: {
      recorded: number;
      skipped: {
        lineIndex: number;
        reasonCode?: SkippedReasonCode;
        reason?: string;
      }[];
    },
    decision: 'agree' | 'disagree',
  ) {
    const report = classifyLineDecisionOutcome(outcome);
    if (report.kind === 'silent') return;

    if (report.kind === 'all-skipped') {
      showToast({
        title: gt("Couldn't record that", {
          context:
            'ship-and-debit 849 toast title when no line decision could be recorded',
        }),
        message:
          report.skipped === 1
            ? skipReasonText(report.reasonCode)
            : gt("{count} line(s) couldn't be decided.", {
                context:
                  'ship-and-debit 849 toast detail when several lines could not be decided',
                count: String(report.skipped),
              }),
        color: 'warning',
      });
      return;
    }

    const title =
      decision === 'agree'
        ? gt('{count} line(s) accepted', {
            context:
              'ship-and-debit 849 toast title after accepting lines in bulk',
            count: String(report.recorded),
          })
        : gt('{count} line(s) disputed', {
            context:
              'ship-and-debit 849 toast title after disputing lines in bulk',
            count: String(report.recorded),
          });

    if (report.kind === 'recorded') {
      showToast({ title, color: 'success' });
      return;
    }

    // Partial success is a warning, not a success: the user asked for more than
    // they got, and the difference is theirs to notice.
    showToast({
      title,
      message:
        report.skipped === 1
          ? gt('1 skipped — {reason}', {
              context:
                'ship-and-debit 849 toast detail for a partially applied bulk decision, with the reason',
              reason: String(skipReasonText(report.reasonCode)),
            })
          : gt('{count} skipped', {
              context:
                'ship-and-debit 849 toast detail for a partially applied bulk decision',
              count: String(report.skipped),
            }),
      color: 'warning',
    });
  }

  /**
   * Record and clear are separate mutations that write the SAME row, and the
   * dispute path fires `disagree` without awaiting it — so a Dispute followed
   * promptly by an Undo puts two writes in flight at once, and nothing about
   * HTTP guarantees they arrive in click order. If the clear lands first it
   * finds nothing to remove, the record then persists `disagree`, and the line
   * comes back disputed after a reload.
   *
   * A shared mutation scope makes TanStack Query run them SERIALLY in the order
   * they were called, so the last click is the last write.
   */
  const decisionScope = { id: `sd849-line-decisions-${sd849ResponseId ?? ''}` };

  const recordMutation = useMutation({
    ...orpc.shipAndDebit.edi849Responses.recordLineDecision.mutationOptions(),
    scope: decisionScope,
    onSuccess: (data, variables) => {
      applyResult(data);
      // A bulk decision routinely succeeds partially — the server records what
      // it can and returns the rest — so report what happened, not just "saved".
      reportDecisionOutcome(data, variables.decision);
    },
    onError,
  });

  const clearMutation = useMutation({
    ...orpc.shipAndDebit.edi849Responses.clearLineDecisions.mutationOptions(),
    scope: decisionScope,
    onSuccess: applyResult,
    onError,
  });

  const decisions = query.data?.decisions ?? NO_DECISIONS;
  const byLineIndex = useMemo(() => {
    const map = new Map<number, SD849LineDecision>();
    for (const decision of decisions) map.set(decision.lineIndex, decision);
    return map;
  }, [decisions]);

  // Every mutator is a no-op when this response's supplier doesn't record
  // decisions, so a caller (e.g. the Dispute button, which every EDI supplier
  // has) can call them unconditionally without provoking a 403 toast.
  const decisionsEnabled = query.data?.decisionsEnabled ?? false;

  function record({
    lineIndexes,
    decision,
    note,
  }: {
    lineIndexes: number[];
    decision: 'agree' | 'disagree';
    note?: string;
  }) {
    if (!sd849ResponseId || !decisionsEnabled || lineIndexes.length === 0) {
      return;
    }
    recordMutation.mutate({
      sd849ResponseId,
      lineIndexes,
      decision,
      // Omitted rather than sent empty: the server treats a supplied empty
      // string as "clear the note", so passing '' on a bulk decision would
      // wipe reasons already written on those lines.
      ...(note?.trim() ? { note: note.trim() } : {}),
    });
  }

  function clearDecisions(lineIndexes: number[]) {
    if (!sd849ResponseId || !decisionsEnabled || lineIndexes.length === 0) {
      return;
    }
    clearMutation.mutate({ sd849ResponseId, lineIndexes });
  }

  /**
   * The awaited bulk write: resolve with the line indexes that now carry
   * `decision`, read from the state the server returned.
   *
   * Never rejects — the mutation's own `onError` has already reported the
   * failure, and the caller's question is "which lines may I act on?", to which
   * the answer on failure is "none".
   */
  async function recordAwaited({
    lineIndexes,
    decision,
    note,
  }: {
    lineIndexes: number[];
    decision: 'agree' | 'disagree';
    note?: string;
  }): Promise<number[]> {
    if (!sd849ResponseId || !decisionsEnabled || lineIndexes.length === 0) {
      return [];
    }
    try {
      const result = await recordMutation.mutateAsync({
        sd849ResponseId,
        lineIndexes,
        decision,
        ...(note?.trim() ? { note: note.trim() } : {}),
      });
      const requested = new Set(lineIndexes);
      // Read the post-write state rather than assuming the request's own list:
      // the server skips lines it can't decide, and those must not be treated
      // as recorded.
      return result.decisions.reduce<number[]>((recorded, d) => {
        if (requested.has(d.lineIndex) && d.decision === decision) {
          recorded.push(d.lineIndex);
        }
        return recorded;
      }, []);
    } catch {
      return [];
    }
  }

  /** The same, for clearing: resolves with the lines that no longer carry one. */
  async function clearAwaited(lineIndexes: number[]): Promise<number[]> {
    if (!sd849ResponseId || !decisionsEnabled || lineIndexes.length === 0) {
      return [];
    }
    try {
      const result = await clearMutation.mutateAsync({
        sd849ResponseId,
        lineIndexes,
      });
      const stillDecided = new Set(result.decisions.map((d) => d.lineIndex));
      return lineIndexes.filter((index) => !stillDecided.has(index));
    } catch {
      return [];
    }
  }

  return {
    enabled: decisionsEnabled,
    decisionFor: (lineIndex) => byLineIndex.get(lineIndex),
    isAgreed: (lineIndex) => byLineIndex.get(lineIndex)?.decision === 'agree',
    agree: (lineIndex, note) =>
      record({ lineIndexes: [lineIndex], decision: 'agree', note }),
    disagree: (lineIndex, note) =>
      record({ lineIndexes: [lineIndex], decision: 'disagree', note }),
    // Deliberately NOT gated on the cache holding a decision for this line: the
    // dispute path records `disagree` fire-and-forget, so an immediate undo runs
    // before that decision lands in the cache, and skipping the call here would
    // leave the row behind. The backend clear is a no-op when there's nothing to
    // clear.
    clear: (lineIndex) => clearDecisions([lineIndex]),
    agreeMany: (lineIndexes, note) =>
      recordAwaited({ lineIndexes, decision: 'agree', note }),
    disagreeMany: (lineIndexes, note) =>
      recordAwaited({ lineIndexes, decision: 'disagree', note }),
    clearMany: clearAwaited,
    isSaving: recordMutation.isPending || clearMutation.isPending,
    isLoaded: query.isSuccess,
    all: decisions,
    counts: {
      agreed: decisions.filter((d) => d.decision === 'agree').length,
      disagreed: decisions.filter((d) => d.decision === 'disagree').length,
    },
  };
}

/**
 * The message shown when this tab loses sight of a resubmission that may well
 * still be running.
 *
 * Worded to stop the instinctive retry, which is the whole reason this flow
 * moved off the request: a resubmission that "failed" in the browser but
 * succeeded server-side leaves the original lines locked as RESUBMITTED, so the
 * next click is refused with "N transaction(s) are not pending resubmission" —
 * a real-looking error for work that already went out.
 */
const RESUBMIT_LOST_SIGHT_MESSAGE =
  'The resubmission is still processing in the background. Check the 844 Submissions page in a few minutes — do not resubmit these transactions again.';

const RESUBMIT_DEFAULT_FAILURE_MESSAGE =
  'The resubmission failed. Review the 849 response and try again.';

/** What the caller renders once the resubmission settles. */
export interface ResubmitRejectedOutcome {
  /** The unchanged-data guard stopped it; nothing was started. */
  blockedByUnchangedData: boolean;
  unchangedIds: string[];
  /** The finished resubmission — null when blocked. */
  result: Sd849ResubmitResult | null;
}

/**
 * Start the resubmission and wait for its background job to settle.
 *
 * The clone + send runs in Temporal for the same reason the 844 batch send
 * does: it ends in the same `submitTransactions` call, so a resubmission
 * spanning many transactions and DUNS locations outlives the browser's ~300s
 * fetch limit. Closing the tab no longer interrupts anything; this poll is only
 * how THIS tab learns the outcome.
 */
async function resubmitAndAwaitResult(
  input: ResubmitRejectedInput,
): Promise<ResubmitRejectedOutcome> {
  const started =
    await rpcClient.shipAndDebit.edi849Responses.resubmitRejected(input);

  // Nothing was started, so there is no job to poll — the caller re-asks the
  // user and calls again with `confirmUnchanged`.
  if (started.blockedByUnchangedData || started.backgroundJobId === null) {
    return {
      blockedByUnchangedData: true,
      unchangedIds: started.unchangedIds,
      result: null,
    };
  }

  const waited = await waitForBackgroundJob(started.backgroundJobId);
  if (waited.outcome === 'vanished') {
    throw new Error(RESUBMIT_LOST_SIGHT_MESSAGE);
  }
  if (waited.outcome === 'failed') {
    throw new Error(waited.errorMessage ?? RESUBMIT_DEFAULT_FAILURE_MESSAGE);
  }

  return {
    blockedByUnchangedData: false,
    unchangedIds: [],
    result: parseSd849ResubmitResult(waited.job.payload),
  };
}

/**
 * What the user sees if the job succeeded but its payload is unreadable to this
 * build (the backend wrote a shape this frontend doesn't understand).
 *
 * The job status is `Succeeded` by the time we parse, so the resubmission has
 * already gone out. Letting a raw `ZodError` reach the "Resubmission failed"
 * toast would show a JSON issue dump for work that actually succeeded — and
 * invite the retry this whole flow exists to prevent.
 */
const RESUBMIT_UNREADABLE_RESULT_MESSAGE =
  'The resubmission finished, but this page could not read its result. Check the 844 Submissions page — do not resubmit these transactions again.';

/**
 * Pull the resubmission result off a succeeded job's payload.
 *
 * Exported for tests. Throws a worded error when the payload carries no parsable
 * result; the work already succeeded, so the message must not read as a failed
 * send.
 */
export function parseSd849ResubmitResult(
  payload: Record<string, unknown>,
): Sd849ResubmitResult {
  const parsed = Sd849ResubmitResultSchema.safeParse(payload.result);
  if (!parsed.success) {
    throw new Error(RESUBMIT_UNREADABLE_RESULT_MESSAGE);
  }
  return parsed.data;
}

/**
 * Resubmit disputed 849 lines as a new 844 submission, resolving with the full
 * result once the background run finishes — so callers keep the same result
 * shape the old synchronous endpoint returned.
 */
export function useResubmitRejected() {
  return useMutation<ResubmitRejectedOutcome, Error, ResubmitRejectedInput>({
    mutationFn: resubmitAndAwaitResult,
  });
}
